import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useGetMeetings from "../../hooks/useGetMeetings";
import BackBtn from "../../widgets/BackBtn";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { getHeader, toastError } from "../../utils/util";
import { LoadingButton } from "@mui/lab";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
  },
}));

const VisitorRequests = ({ status = "" }) => {
  const { data, isLoading, isError, error, refetch } = useGetMeetings(
    1,
    30,
    "",
    status
  );
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [updatingRequest, setUpdatingRequest] = useState(false);

  // Format timestamp to readable date/time
  const formatDateTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  // Handle row click
  const handleRowClick = (meeting) => {
    setSelectedMeeting(meeting);
  };

  // Close dialog
  const handleClose = () => {
    setSelectedMeeting(null);
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle error state
  if (isError) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto", bgcolor: "#f5f5f5" }}>
        <Alert severity="error">
          Error loading meetings: {error?.message || "Something went wrong"}
        </Alert>
      </Box>
    );
  }

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto", bgcolor: "#f5f5f5" }}>
        <BackBtn />
        <Typography variant="h6" align="center">
          No meeting requests found
        </Typography>
      </Box>
    );
  }
  async function updateMeetingRequest(status, id) {
    try {
      setUpdatingRequest(true);
      const response = await axios.post(
        `${API_ENDPOINT}user/update-meeting-request?id=${id}`,
        { status },
        getHeader()
      );
      setSelectedMeeting(null);
      refetch();
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setUpdatingRequest(false);
    }
  }

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <BackBtn />
      <Box sx={{ p: 3, maxWidth: 1200, margin: "0 auto" }}>
        <Typography variant="h4" sx={{ py: 3 }}>
          Visitor Requests
        </Typography>
        {/* Meetings Table */}
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Target Person</TableCell>
                <TableCell>Visitor</TableCell>
                <TableCell>Meeting Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Company</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((meeting) => (
                <StyledTableRow
                  key={meeting._id}
                  onClick={() => handleRowClick(meeting)}
                >
                  <TableCell>{meeting.targetPerson.name}</TableCell>
                  <TableCell>{meeting.meeter.name}</TableCell>
                  <TableCell>{formatDateTime(meeting.timeFrom)}</TableCell>
                  <TableCell>
                    <Chip
                      label={meeting.status}
                      color={
                        meeting.status === "accepted" ? "success" : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{meeting.organisation.companyName}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Details Dialog */}
        <Dialog
          open={!!selectedMeeting}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
        >
          {selectedMeeting && (
            <>
              <DialogTitle>
                <Typography variant="h5" component="div">
                  Meeting Details
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={3}>
                  {/* Target Person */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Target Person
                      </Typography>
                      <Typography>
                        ID: {selectedMeeting.targetPerson._id}
                      </Typography>
                      <Typography>
                        Name: {selectedMeeting.targetPerson.name}
                      </Typography>
                      <Typography>
                        Email: {selectedMeeting.targetPerson.email}
                      </Typography>
                      <Avatar
                        src={selectedMeeting.targetPerson.profileImage}
                        sx={{ width: 100, height: 100, mt: 2 }}
                      />
                    </Box>
                  </Grid>

                  {/* Visitor */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Visitor
                      </Typography>
                      <Typography>
                        Name: {selectedMeeting.meeter.name}
                      </Typography>
                      <Typography>
                        Email: {selectedMeeting.meeter.email}
                      </Typography>
                      <Avatar
                        src={selectedMeeting.meeter.profileImage}
                        sx={{ width: 100, height: 100, mt: 2 }}
                      />
                    </Box>
                  </Grid>

                  {/* Meeting Details */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Meeting Details
                      </Typography>
                      <Typography>ID: {selectedMeeting._id}</Typography>
                      <Typography>
                        Company: {selectedMeeting.organisation.companyName}
                      </Typography>
                      <Typography>
                        Barcode ID: {selectedMeeting.organisation.barcodeId}
                      </Typography>
                      <Typography>
                        Meeting Date:{" "}
                        {formatDateTime(selectedMeeting.meetingDate)}
                      </Typography>
                      <Typography>
                        From: {formatDateTime(selectedMeeting.timeFrom)}
                      </Typography>
                      <Typography>
                        To: {formatDateTime(selectedMeeting.timeTo)}
                      </Typography>
                      <Typography>
                        Status:{" "}
                        <Chip
                          label={selectedMeeting.status}
                          color={
                            selectedMeeting.status === "accepted"
                              ? "success"
                              : selectedMeeting.status === "pending"
                              ? "warning"
                              : "default"
                          }
                          size="small"
                        />
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Entry/Exit Details */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Entry/Exit Details
                      </Typography>
                      <Typography>
                        Created On: {formatDateTime(selectedMeeting.createdOn)}
                      </Typography>
                      <Typography>
                        Updated On: {formatDateTime(selectedMeeting.updatedOn)}
                      </Typography>
                      {selectedMeeting.entryStatus && (
                        <>
                          <Typography>
                            Entry Status: {selectedMeeting.entryStatus}
                          </Typography>
                          <Typography>
                            Entry Time:{" "}
                            {formatDateTime(selectedMeeting.entryTimeStamp)}
                          </Typography>
                        </>
                      )}
                      {selectedMeeting.exitStatus && (
                        <>
                          <Typography>
                            Exit Status: {selectedMeeting.exitStatus}
                          </Typography>
                          <Typography>
                            Exit Time:{" "}
                            {formatDateTime(selectedMeeting.exitTimeStamp)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  {selectedMeeting.status === "pending" && (
                    <>
                      <LoadingButton
                        variant="contained"
                        color="success"
                        loading={updatingRequest}
                        startIcon={<CheckIcon />}
                        onClick={async () => {
                          await updateMeetingRequest(
                            "accept",
                            selectedMeeting._id
                          );
                        }}
                        sx={{ minWidth: 100 }}
                      >
                        Accept
                      </LoadingButton>
                      <LoadingButton
                        loading={updatingRequest}
                        variant="contained"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={async () => {
                          await updateMeetingRequest(
                            "reject",
                            selectedMeeting._id
                          );
                        }}
                        sx={{ minWidth: 100 }}
                      >
                        Reject
                      </LoadingButton>
                    </>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ minWidth: 100 }}
                  >
                    Close
                  </Button>
                </Box>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </Box>
  );
};

export default VisitorRequests;
